import { createAuthThunks } from "@iotos/ims-auth-sdk/redux";
import {
  createAsyncThunk,
  createSlice,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";

import { API_ENDPOINT } from "../../config/api";
import { PATHS } from "../../constants";
import { IAppUsersDto } from "../../modules/app-users/shared/dtos/app-users";
import { clearFiltersAction } from "../../modules/filter/store/filters.slice";
import { setInfoData } from "../../modules/info-data/info-data-actions";
import { clearApiCache } from "../../modules/offline/shared/utils/clear-api-cache";
import { fetchWithAuth } from "../../shared/api/fetch-with-auth";
import type { RootState } from "../../store-types";
import { imsAuthService, SessionCheckError } from "../shared/ims-auth-service";
import { IAuthState, SessionCheckFailure } from "../shared/interfaces/auth-state";
import { getCurrentRelativeUrl } from "../shared/return-url";

import { AUTH_MODULE_NAME } from "./constants";

/** Тунки управления сессией, сгенерированные `@iotos/ims-auth-sdk/redux`. */
export const { signOut: imsSignOut, loadCurrentUser: getCurrentUser } = createAuthThunks({
  authService: imsAuthService,
});

/**
 * Проверка сессии. Свой тунк вместо SDK-шного: тот сводит любой сбой к одному
 * `INITIALIZATION_FAILED`, а нам нужно отличать отказ (403) от технической ошибки,
 * чтобы не уводить пользователя на вход по кругу.
 */
export const initializeAuth = createAsyncThunk<
  Awaited<ReturnType<typeof imsAuthService.initialize>>,
  void,
  { rejectValue: { failure: SessionCheckFailure; detail: string } }
>(`${AUTH_MODULE_NAME}/initialize`, async (_, { rejectWithValue }) => {
  try {
    return await imsAuthService.initialize();
  } catch (error) {
    const isForbidden = error instanceof SessionCheckError && error.isForbidden;
    return rejectWithValue({
      failure: isForbidden ? "forbidden" : "error",
      detail: error instanceof Error ? error.message : "Не удалось проверить сессию",
    });
  }
});

/** Хозяйство по умолчанию из записи AppUser текущего пользователя. */
export const fetchAppUserProfile = createAsyncThunk<
  Pick<IAppUsersDto, "defaultFarmId">,
  string
>(`${AUTH_MODULE_NAME}/fetchAppUserProfile`, async (userId) => {
  const response = await fetchWithAuth(`${API_ENDPOINT}/AppUsersV2/${userId}`);
  if (!response.ok) {
    throw await response.json();
  }
  return response.json();
});

export const initialState: IAuthState = {
  isAuthenticated: false,
  user: null,
  isLoadingUserDto: false,
  error: null,
  sessionCheckFailure: null,
};

export const authSlice = createSlice({
  name: AUTH_MODULE_NAME,
  initialState,
  reducers: {
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoadingUserDto = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = action.payload !== null;
        state.user = action.payload ?? null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.error = action.payload?.detail ?? "Не удалось загрузить пользователя";
        authSlice.caseReducers.logoutSuccess(state);
      })
      .addCase(initializeAuth.pending, (state) => {
        state.isLoadingUserDto = true;
        state.error = null;
        state.sessionCheckFailure = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = action.payload.isValid;
        state.user = action.payload.isValid ? (action.payload.user ?? null) : null;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload?.detail ?? "Не удалось проверить сессию";
        state.sessionCheckFailure = action.payload?.failure ?? "error";
      })
      .addCase(imsSignOut.pending, (state) => {
        state.isLoadingUserDto = true;
      })
      .addCase(imsSignOut.fulfilled, (state) => {
        state.isLoadingUserDto = false;
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(imsSignOut.rejected, (state, action) => {
        state.isLoadingUserDto = false;
        state.error = action.payload?.detail ?? "Не удалось завершить сессию";
      })
      .addCase(fetchAppUserProfile.fulfilled, (state, action) => {
        if (state.user?.userId === action.meta.arg) {
          state.user.defaultFarmId = action.payload.defaultFarmId;
        }
      });
  },
});

export const { logoutSuccess } = authSlice.actions;

export const authSliceReducer = authSlice.reducer;

const resetUserState = (
  dispatch: ThunkDispatch<RootState, unknown, UnknownAction>
): void => {
  dispatch(logoutSuccess());
  dispatch(clearFiltersAction());
  dispatch(setInfoData({ initialDictionariesLoaded: false }));
};

/**
 * Явный выход: чистит состояние и кеш справочников и отдаёт браузер BFF, который
 * завершает сессию в IMS и возвращает на страницу «Вы вышли».
 */
export const logout = createAsyncThunk<void, void, { state: RootState }>(
  `${AUTH_MODULE_NAME}/logout`,
  async (_, { dispatch }) => {
    resetUserState(dispatch);
    await clearApiCache();
    await imsAuthService.signOutTo(PATHS.SIGNED_OUT);
  }
);

/**
 * Действовавшая сессия пропала (401 от API). Показывает страницу «Сессия завершена»
 * вместо немедленного входа: так пользователь видит, что произошло, а пачка
 * параллельных 401 и сигналы из других вкладок не запускают вход по кругу.
 * Выход на BFF не зовём: он гасит SSO-сессию во всех приложениях IMS.
 * Срабатывает один раз и только для вошедшего пользователя — первичную проверку
 * сессии обрабатывает `useAuthGate`.
 */
export const sessionExpired = createAsyncThunk<void, void, { state: RootState }>(
  `${AUTH_MODULE_NAME}/sessionExpired`,
  async (_, { dispatch, getState }) => {
    if (!getState()[AUTH_MODULE_NAME].isAuthenticated) {
      return;
    }
    const returnUrl = encodeURIComponent(getCurrentRelativeUrl());
    resetUserState(dispatch);
    await clearApiCache();
    window.location.assign(`${PATHS.SESSION_EXPIRED}?returnUrl=${returnUrl}`);
  }
);
